import { MediaLibrarySortField } from "src/types/mediaLibrary";
import { MediaFile } from "src/types/media";
import { MediaResponse } from "src/types/experiences/storyboardSettings/primitives";

export type Source = "my-uploads" | "pexels" | "brand-media";
export type LibraryMediaType = "image" | "video";

export const SOURCES: { label: string; value: Source; icon?: string }[] = [
  {
    label: "My uploads",
    value: "my-uploads",
    icon: "/images/common/banner/sources/paperflite.svg",
  },
  {
    label: "Pexels",
    value: "pexels",
    icon: "/images/common/banner/sources/pexels.svg",
  },
  {
    label: "Brand media",
    value: "brand-media",
  },
];

// Allowed image MIME types for social image upload (mirrors legacy formats).
export const IMAGE_MIME_TYPES = [
  "image/jpg",
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/bmp",
  "image/svg",
  "image/svg+xml",
];

// Allowed video MIME types when video uploads are enabled (e.g. appearance background).
export const VIDEO_MIME_TYPES = [
  "video/mp4",
];

export const SORT_OPTIONS: {
  label: string;
  value: MediaLibrarySortField;
}[] = [
  { label: "Date", value: "modifiedDate" },
  { label: "Name", value: "name" },
];

// eslint-disable-next-line no-undef
const IMAGE_CDN = (process.env.REACT_APP_IMAGE_CDN ?? "").replace(/\/+$/, "");

export const toCdnUrl = (url: string | undefined | null): string => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  const path = url.startsWith("/") ? url : `/${url}`;
  return `${IMAGE_CDN}${path}`;
};

export const toCardThumbnail = (url: string | undefined | null): string => {
  const resolved = toCdnUrl(url);
  if (!resolved.includes("ik.imagekit.io")) return resolved;
  try {
    const resized = new URL(resolved);
    resized.searchParams.set("tr", "w-280,h-280");
    return resized.toString();
  } catch {
    return resolved;
  }
};

export const toPexelsThumbnail = (url: string | undefined | null): string => {
  if (!url) return "";
  if (!url.includes("images.pexels.com")) return url;
  try {
    const resized = new URL(url);
    resized.searchParams.set("w", "280");
    resized.searchParams.set("h", "280");
    resized.searchParams.set("dpr", "2");
    resized.searchParams.set("fit", "crop");
    return resized.toString();
  } catch {
    return url;
  }
};

const fetchUrlAsFile = async (url: string): Promise<File> => {
  const response = await fetch(url);
  if (!response.ok)
    throw new Error(`failed to fetch image (${response.status})`);
  const blob = await response.blob();
  const extension = (blob.type.split("/")[1] || "jpg").split("+")[0];
  return new File([blob], `pexels-${Date.now()}.${extension}`, {
    type: blob.type || "image/jpeg",
  });
};

export const resolveFileToUpload = async ({
  pendingCropFile,
  pendingExternalUpload,
  mediaUrl,
}: {
  pendingCropFile: File | null;
  pendingExternalUpload: boolean;
  mediaUrl?: string | null;
}): Promise<File | null> => {
  if (pendingCropFile) return pendingCropFile;
  if (!pendingExternalUpload || !mediaUrl) return null;
  try {
    return await fetchUrlAsFile(mediaUrl);
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const toMediaResponse = (
  media: MediaFile | null,
): MediaResponse | null => {
  if (!media) return null;
  return {
    id: media.id,
    name: media?.name ?? "",
    contentType: media?.contentType ?? "",
    type: media?.type ?? "",
    width: media?.width ?? 0,
    height: media?.height ?? 0,
    url: media?.full ?? "",
  };
};

export const applyCropToImage = (
  src: string,
  crop: { x: number; y: number; width: number; height: number } | null,
): Promise<{
  url: string;
  width: number;
  height: number;
  contentType: string;
  blob: Blob | null;
}> =>
  new Promise((resolve, reject) => {
    // No crop region — use the original image as-is.
    if (!crop) {
      resolve({ url: src, width: 0, height: 0, contentType: "", blob: null });
      return;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(crop.width));
      canvas.height = Math.max(1, Math.round(crop.height));
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("canvas context unavailable"));
        return;
      }
      ctx.drawImage(
        img,
        crop.x,
        crop.y,
        crop.width,
        crop.height,
        0,
        0,
        canvas.width,
        canvas.height,
      );
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("blob creation failed"));
          return;
        }
        resolve({
          url: URL.createObjectURL(blob),
          width: canvas.width,
          height: canvas.height,
          contentType: blob.type,
          blob,
        });
      }, "image/png");
    };
    // Crop failed to load — fall back to the original image url.
    img.onerror = () =>
      resolve({ url: src, width: 0, height: 0, contentType: "", blob: null });
    img.src = src;
  });
